/**
 * ReceiptsPage — AR aging detail with drill-down by entity, customer, aging bucket.
 */

import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui';
import { useARAgingPaginated, useARSummary } from './api';
import { formatCurrency } from './utils';
import { cn } from '../../shared/lib/cn';
import { SortableHeader } from './SortableHeader';
import type { AgingBucket } from './types';

const BUCKETS: AgingBucket[] = ['Current', '1-30', '31-60', '61-90', 'Over 90'];

export default function ReceiptsPage() {
  const [page, setPage] = useState(1);
  const [bucket, setBucket] = useState<string>('');
  const [customer, setCustomer] = useState('');
  const [sortBy, setSortBy] = useState('open_balance');
  const [sortDir, setSortDir] = useState('DESC');

  const { data: summary } = useARSummary();
  const { data: aging, isLoading } = useARAgingPaginated({
    page,
    page_size: 25,
    bucket: bucket || undefined,
    customer: customer || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
  });

  const totalPages = aging ? Math.ceil(aging.total / aging.page_size) : 1;

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(d => d === 'DESC' ? 'ASC' : 'DESC');
    } else {
      setSortBy(field);
      setSortDir('ASC');
    }
    setPage(1);
  };

  return (
    <div className="space-y-6 mt-2">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card>
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Total AR</p>
              <p className="text-lg font-bold text-charcoal dark:text-gray-100">{formatCurrency(summary.total_ar, true)}</p>
            </CardContent>
          </Card>
          {BUCKETS.map(b => (
            <Card key={b}>
              <CardContent className="p-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{b}</p>
                <p className="text-lg font-bold text-charcoal dark:text-gray-100">
                  {formatCurrency(summary.by_bucket[b] ?? 0, true)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={bucket}
          onChange={e => { setBucket(e.target.value); setPage(1); }}
          className="text-sm border border-gray-200 dark:border-dark-border rounded-lg px-3 py-1.5 bg-white dark:bg-dark-surface text-charcoal dark:text-gray-200"
        >
          <option value="">All Buckets</option>
          {BUCKETS.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <input
          type="text"
          placeholder="Search customer..."
          value={customer}
          onChange={e => { setCustomer(e.target.value); setPage(1); }}
          className="text-sm border border-gray-200 dark:border-dark-border rounded-lg px-3 py-1.5 bg-white dark:bg-dark-surface text-charcoal dark:text-gray-200 w-48"
        />
        <span className="text-xs text-gray-400 ml-auto">
          {aging ? `${aging.total} records` : ''}
        </span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2">
                  <SortableHeader label="Customer" field="customer_name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Invoice #" field="invoice_number" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Due Date" field="due_date" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Bucket" field="aging_bucket" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Days Out" field="days_outstanding" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortableHeader label="Balance" field="open_balance" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortableHeader label="Behavior" field="customer_behavior_class" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-border/50">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-400">Loading...</td>
                  </tr>
                ) : !aging?.items.length ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-400">No records found</td>
                  </tr>
                ) : aging.items.map(rec => (
                  <tr key={rec.id} className="hover:bg-gray-50 dark:hover:bg-dark-surface-2/50">
                    <td className="py-2 px-3 text-charcoal dark:text-gray-200">{rec.customer_name}</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{rec.invoice_number}</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{rec.due_date}</td>
                    <td className="py-2 px-3">
                      <span className={cn(
                        'inline-block px-2 py-0.5 rounded text-xs font-medium',
                        rec.aging_bucket === 'Current' ? 'bg-emerald-100 text-emerald-700' :
                        rec.aging_bucket === '1-30' ? 'bg-blue-100 text-blue-700' :
                        rec.aging_bucket === '31-60' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700',
                      )}>
                        {rec.aging_bucket}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-gray-600 dark:text-gray-400">{rec.days_outstanding}</td>
                    <td className="py-2 px-3 text-right tabular-nums font-medium text-charcoal dark:text-gray-200">
                      {formatCurrency(rec.open_balance)}
                    </td>
                    <td className="py-2 px-3 text-gray-500 dark:text-gray-400 text-xs">
                      {rec.customer_behavior_class || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-dark-border">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="text-xs px-3 py-1 rounded bg-gray-100 dark:bg-dark-surface-2 disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="text-xs px-3 py-1 rounded bg-gray-100 dark:bg-dark-surface-2 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-gray-400 dark:text-gray-500 italic">
        * Collection timing uses bucket-average estimates. Customer-specific payment behavior data, when available, will improve accuracy.
      </p>
    </div>
  );
}
